import { Placement } from '@popperjs/core';
import { MutableRefObject, RefObject, useMemo } from 'react';
import min from 'lodash/min';
import max from 'lodash/max';

import useRect from '../../../../hooks/#useRect';

interface UseDropListPlacementOptions {
  scrollContainer: RefObject<HTMLElement>;
  popperRef: MutableRefObject<HTMLDivElement>;
  targetRef: MutableRefObject<HTMLElement>;
  isOpened: boolean;
  offsetFromEdge: number;
}

const useDropListPlacement = ({ scrollContainer, popperRef, isOpened, targetRef, offsetFromEdge }: UseDropListPlacementOptions) => {
  const popperRefRect = useRect(popperRef, isOpened);
  const targetRect = useRect(targetRef, isOpened);
  const scrollContainerRect = useRect(scrollContainer, isOpened);

  const popperheight = useMemo(() => popperRefRect && Math.ceil(popperRefRect?.height), [popperRefRect]);

  const [maxPopperHeight, placement]: [number, Placement] = useMemo(() => {
    if (!targetRect || !popperRefRect) {
      return [null, 'bottom'];
    }

    const targetHeight = targetRef?.current?.clientHeight;

    const containerHeight = min([scrollContainer.current.clientHeight, window.innerHeight]);

    const bottomSpace = Math.ceil(containerHeight - targetRect.top - targetHeight - offsetFromEdge);

    if (popperheight < bottomSpace) {
      return [null, 'bottom'];
    }

    const containerTop = max([scrollContainerRect.top, 0]);

    const topSpace = Math.ceil(targetRect.top - offsetFromEdge - containerTop);

    if (popperheight < topSpace) {
      return [null, 'top'];
    }

    if (bottomSpace >= topSpace) {
      return [bottomSpace, 'bottom'];
    }

    return [topSpace, 'top'];
  }, [offsetFromEdge, popperRefRect, popperheight, scrollContainer, scrollContainerRect?.top, targetRect, targetRef]);

  return {
    maxPopperHeight, placement,
  };
};

export default useDropListPlacement;
